import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Stack,
} from '@mui/material';
import {
  FlightTakeoff as FlightTakeoffIcon,
  ToggleOn as ToggleOnIcon,
  DateRange as DateRangeIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useDispatch, useSelector } from 'react-redux';
import {
  paymentOrchestrationRulesActions,
  selectAvailableRuleSources,
  selectAvailableRuleStatus,
  selectFilteredSource,
  selectFilteredStatus,
  selectFilteredEffectiveStartDate,
  selectFilteredEffectiveEndDate,
} from '../../store/payment-orchestration-rules.slice';
import { PAYMENT_ORCHESTRATION_RULE_DATE_FORMAT } from '../../payment-orchestration.constants';

function FilterListItemHeader({
  title,
  onClick,
  isExpanded,
  isDisabled = false,
  icon,
}) {
  return (
    <ListItemButton
      onClick={onClick}
      disabled={isDisabled}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={title} />
      {isExpanded && !isDisabled ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </ListItemButton>
  );
}

function FilterListCollapsableListItem({
  isExpanded,
  items,
  selection,
  onSelection,
}) {
  return (
    <Collapse
      in={isExpanded}
      unmountOnExit>
      <List
        component="div"
        sx={{
          maxHeight: '180px',
          overflow: 'scroll',
        }}
        disablePadding>
        {items.map((item) => (
          <ListItemButton
            key={item}
            dense
            sx={{ pl: 4 }}
            onClick={() => onSelection(item)}>
            <ListItemIcon>
              <Checkbox
                checked={selection.includes(item)}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary={item} />
          </ListItemButton>
        ))}
      </List>
    </Collapse>
  );
}

function FilterListCollapsableView({ isExpanded, children }) {
  return <Collapse in={isExpanded}>{children}</Collapse>;
}

export default function OrchestrationRulesFilter({
  isVisible = false,
  onClose,
}) {
  const dispatch = useDispatch();

  const availableRuleSources = useSelector(selectAvailableRuleSources);
  const availableRuleStatus = useSelector(selectAvailableRuleStatus);
  const filteredSource = useSelector(selectFilteredSource);
  const filteredStatus = useSelector(selectFilteredStatus);
  const filteredEffectiveStartDate = useSelector(
    selectFilteredEffectiveStartDate,
  );
  const filteredEffectiveEndDate = useSelector(selectFilteredEffectiveEndDate);

  const [isSourceFilterExpanded, setIsSourceFilterExpanded] = useState(true);
  const [selectedSources, setSelectedSources] = useState(filteredSource);

  const [isStatusFilterExpanded, setIsStatusFilterExpanded] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState(filteredStatus);

  const [isEffectivityFilterExpanded, setIsEffectivityFilterExpanded] =
    useState(true);
  const [selectedEffectiveStartDate, setSelectedEffectiveStartDate] = useState(
    filteredEffectiveStartDate,
  );
  const [selectedEffectiveEndDate, setSelectedEffectiveEndDate] = useState(
    filteredEffectiveEndDate,
  );

  // Default Values
  useEffect(() => {
    setSelectedSources(filteredSource);
  }, [filteredSource]);

  useEffect(() => {
    setSelectedStatus(filteredStatus);
  }, [filteredStatus]);

  useEffect(() => {
    setSelectedEffectiveStartDate(filteredEffectiveStartDate);
  }, [filteredEffectiveStartDate]);

  useEffect(() => {
    setSelectedEffectiveEndDate(filteredEffectiveEndDate);
  }, [filteredEffectiveEndDate]);

  const handleClose = () => {
    onClose(false);

    // Expand options on exit
    setIsSourceFilterExpanded(true);
    setIsStatusFilterExpanded(true);
    setIsEffectivityFilterExpanded(true);

    // Set default values
    setSelectedStatus(filteredStatus);
    setSelectedSources(filteredSource);
    setSelectedEffectiveStartDate(filteredEffectiveStartDate);
    setSelectedEffectiveEndDate(filteredEffectiveEndDate);
  };

  const onApplyClickHandler = () => {
    const {
      setFilterSources,
      setFilterStatuses,
      setFilterEffectiveStartDate,
      setFilterEffectiveEndDate,
    } = paymentOrchestrationRulesActions;

    dispatch(setFilterSources(selectedSources));
    dispatch(setFilterStatuses(selectedStatus));
    dispatch(setFilterEffectiveStartDate(selectedEffectiveStartDate));
    dispatch(setFilterEffectiveEndDate(selectedEffectiveEndDate));

    handleClose();
  };

  const onResetClickHandler = () => {
    const { resetFilters } = paymentOrchestrationRulesActions;

    dispatch(resetFilters());

    handleClose();
  };

  const onSourceSelection = (source) => {
    const isAdded = selectedSources.includes(source);
    let newSelectedSources = [];

    if (isAdded) {
      newSelectedSources = selectedSources.filter(
        (addedSource) => addedSource !== source,
      );
    } else {
      newSelectedSources = [...selectedSources, source];
    }

    setSelectedSources(newSelectedSources);
  };

  const onStatusSelection = (status) => {
    const isAdded = selectedStatus.includes(status);
    let newSelectedStatus = [];

    if (isAdded) {
      newSelectedStatus = selectedStatus.filter(
        (addedStatus) => addedStatus !== status,
      );
    } else {
      newSelectedStatus = [...selectedStatus, status];
    }

    setSelectedStatus(newSelectedStatus);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isVisible}
      onClose={handleClose}>
      <DialogTitle>Orchestration Rules Filter</DialogTitle>
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}>
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{
          p: 0,
        }}>
        <List component="nav">
          {/* Filter: Source */}
          <FilterListItemHeader
            title="Source"
            icon={<FlightTakeoffIcon />}
            isDisabled={!availableRuleSources.length}
            onClick={() => setIsSourceFilterExpanded(!isSourceFilterExpanded)}
            isExpanded={isSourceFilterExpanded}
          />
          <FilterListCollapsableListItem
            isExpanded={isSourceFilterExpanded}
            items={availableRuleSources}
            selection={selectedSources}
            onSelection={onSourceSelection}
          />

          <Divider />

          {/* Filter: Status */}
          <FilterListItemHeader
            title="Status"
            icon={<ToggleOnIcon />}
            isDisabled={!availableRuleStatus.length}
            onClick={() => setIsStatusFilterExpanded(!isStatusFilterExpanded)}
            isExpanded={isStatusFilterExpanded}
          />
          <FilterListCollapsableListItem
            isExpanded={isStatusFilterExpanded}
            items={availableRuleStatus}
            selection={selectedStatus}
            onSelection={onStatusSelection}
          />
          <Divider />

          {/* Filter: Effectivity Range */}
          <FilterListItemHeader
            title="Effectivity Range"
            icon={<DateRangeIcon />}
            onClick={() =>
              setIsEffectivityFilterExpanded(!isEffectivityFilterExpanded)
            }
            isExpanded={isEffectivityFilterExpanded}
          />
          <FilterListCollapsableView isExpanded={isEffectivityFilterExpanded}>
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: '24px',
                padding: '12px 42px',
              }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format={PAYMENT_ORCHESTRATION_RULE_DATE_FORMAT}
                  label="Effective Start Date"
                  value={
                    selectedEffectiveStartDate
                      ? dayjs(selectedEffectiveStartDate)
                      : null
                  }
                  onChange={(newDate) => {
                    setSelectedEffectiveStartDate(newDate);
                    setSelectedEffectiveEndDate(dayjs(newDate).add(1, 'month'));
                  }}
                  slotProps={{
                    field: { fullWidth: true },
                  }}
                  // maxDate={dayjs(selectedEffectiveEndDate)}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format={PAYMENT_ORCHESTRATION_RULE_DATE_FORMAT}
                  label="Effective End Date"
                  value={
                    selectedEffectiveEndDate
                      ? dayjs(selectedEffectiveEndDate)
                      : null
                  }
                  onChange={setSelectedEffectiveEndDate}
                  slotProps={{
                    field: { fullWidth: true },
                  }}
                  minDate={dayjs(selectedEffectiveStartDate)}
                />
              </LocalizationProvider>
            </Stack>
          </FilterListCollapsableView>
        </List>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onResetClickHandler}
          variant="outlined">
          Reset
        </Button>
        <Button
          onClick={onApplyClickHandler}
          variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
